import { AvailabilityEnum } from '../../../../../products/ProductTypes';
import { useProductLock } from '../../../../../products/products-details-v2/hooks/useProductLock';
import { OrderProductGridItem, UpdateItemFormat } from '../../../orderProducts.types';
import {
  HandleSelectPacks,
  HandleSelectSuggestion,
  QuantityByProductSizeId,
} from '../components/orderProductGrid/types/commonGrid.types';
import {
  getPacksWithQuantitiesFromSelectedPacks,
  getQuantitiesFromCommonGrid,
  getQuantitiesFromPacks,
} from '../components/orderProductGrid/utils/commonGrids.utils';
import { useGridStoreApi } from '../store/GridStoreProvider';
import { buildCombinationKey } from '../store/gridStore.utils';
import { produceItemsToStore } from '../store/produceItemsToStore';
import { getQuantityWithinStock } from '../utils/gridQuantity.utils';
import { UseProductGridActionsParams } from './productGridActions.types';

export const useProductGridActions = ({
  grid,
  availability,
  deliveryWindowId,
  orderId,
  productColorId,
  block,
  onApplyQuantities,
  onSelectPacks,
}: UseProductGridActionsParams) => {
  const { isLocked, onClickLock, getLimit, getStockValue } = useProductLock();
  const gridStoreApi = useGridStoreApi();
  const combinationKey = buildCombinationKey({
    productColorId,
    availability: availability as AvailabilityEnum,
    deliveryWindowId,
    orderId,
  });

  const buildUpdateItem = (
    gridItem: OrderProductGridItem,
    quantityByProductSizeId: QuantityByProductSizeId,
  ): UpdateItemFormat => {
    const availableAmount = gridItem.stock?.availableAmount || 0;
    const limit = getLimit({ availableAmount, availability });
    const stockValue = getStockValue({ availableAmount, availability });
    const requestedQuantity = quantityByProductSizeId.get(gridItem.productSizeId) || 0;
    const quantity = gridItem.available ? getQuantityWithinStock(requestedQuantity, stockValue) : 0;

    if (isLocked && limit && quantity > limit) {
      onClickLock();
    }

    return {
      id: gridItem.orderItemId,
      skuId: gridItem.skuId,
      deliveryWindowId,
      quantity,
      availability,
      orderConditionId: gridItem.orderConditionId,
    };
  };

  const applyQuantities = (quantityByProductSizeId: QuantityByProductSizeId) => {
    const updateItems = grid.map((gridItem) => buildUpdateItem(gridItem, quantityByProductSizeId));

    if (onApplyQuantities) {
      onApplyQuantities(updateItems);
      return;
    }

    if (orderId) {
      produceItemsToStore({
        api: gridStoreApi,
        combinationKey,
        items: updateItems.map((item) => ({ ...item, orderId, productColorId })),
      });
    }
  };

  const handleSelectSuggestion: HandleSelectSuggestion = (commonGrid, suggestedPacks) => {
    if (block?.isBlocked) {
      block.onBlocked();
      return;
    }

    applyQuantities(getQuantitiesFromCommonGrid(commonGrid));

    if (onSelectPacks) {
      onSelectPacks(
        suggestedPacks?.length ? getPacksWithQuantitiesFromSelectedPacks(suggestedPacks) : [],
      );
    }
  };

  const applySelectedPacks: HandleSelectPacks = (packs) => {
    if (block?.isBlocked) {
      block.onBlocked();
      return;
    }

    onSelectPacks?.(packs);
    applyQuantities(getQuantitiesFromPacks(packs));
  };

  const blockSelectedPacks: HandleSelectPacks = () => block?.onBlocked();

  const getHandleSelectPacks = (): HandleSelectPacks | undefined => {
    if (onSelectPacks) {
      return applySelectedPacks;
    }

    return block?.isBlocked ? blockSelectedPacks : undefined;
  };

  return {
    handleSelectSuggestion,
    handleSelectPacks: getHandleSelectPacks(),
  };
};
